import React, { useEffect, useState } from "react";
import { ITkRequestIDSelectionDialogProps } from "../../../type/componentsType/TkRequestIDSelectionDialog";
import TkModalWithTitle from "../../atoms/TkModalWithTitle";
import { Box } from "@mui/material";
import TkSearchForm from "../TkSearchForm";
import { schema, searchList, tableHeader } from "./parts";
import {
  BillingTypeFormFacility,
  Prefectures,
} from "../../../assets/data/Constants";
import TkFakeTableLine from "../../atoms/TkFakeTableLine";
import TkTitle from "../../atoms/TkTitle";
import "./_style.scss";
import { httpGetClaimList } from "./api/request";
import { changeAmountFormat } from "../../../utils/useTools";
import TkTable from "../../atoms/TkTable";
import TkRadio from "../../atoms/TkRadio";
import { TkGetCode } from "../../../utils/TkGetCode";

/**
 * TkRequestIDSelectionDialogコンポーネントは、リクエストIDを選択するためのダイアログです。
 * @param open - ダイアログの表示状態。
 * @param onSuccess - 選択が成功した場合のコールバック関数。
 * @param onClose - ダイアログを閉じる場合のコールバック関数。
 * @param clearSaveData - 保存されたデータをクリアする場合のコールバック関数。
 * @param transactionDate - 取引日付。
 * @param depositAmount - 入金額。
 * @param summary - 摘要。
 * @param bankBranch - 銀行/支店。
 */
const TkRequestIDSelectionDialog: React.FC<ITkRequestIDSelectionDialogProps> = (
  props
) => {
  const {
    open,
    onSuccess,
    onClose,
    clearSaveData,
    transactionDate,
    depositAmount,
    summary,
    bankBranch,
    wantToSetValue,
  } = props;
  const [relSearchList, setRelSearchList] = useState<any>([]);
  const [showData, setShowData] = useState<any>({});
  const [searchData, setSearchData] = useState<any>([]);
  const [checkedId, setCheckedId] = useState<string>("");
  const [billingList, setBillingList] = useState<any>([]);

  const handleOnSearch = async (data: any) => {
    try {
      const params = { ...data };
      params.searchBillingAmt = Number(params.searchBillingAmt);

      const res = await httpGetClaimList(params);

      setBillingList(res.billingList ?? []);

      setCheckedId(
        res.billingList?.find((i: any) => i.invoiceId === checkedId)
          ? checkedId
          : ""
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setBillingList(wantToSetValue);
  }, [wantToSetValue]);

  useEffect(() => {
    let item = searchList.find((item) => item.keyword === "searchKbn");

    if (item && "selectPropsList" in item) {
      item.selectPropsList = (() =>
        Object.entries(BillingTypeFormFacility).map(([label, value]) => ({
          label,
          value,
        })))();
    }

    setRelSearchList(searchList);

    const data = {
      取引日付: transactionDate,
      入金額: depositAmount,
      摘要: summary,
      "銀行／支店": bankBranch,
    };

    setShowData(
      Object.entries(data).map(([value, key]) => ({
        key,
        value,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const resList = billingList.map((item: any) => [
      {
        children: (
          <Box className="TkRadioInTkRequestIDSelectionDialogModale">
            <TkRadio
              checked={checkedId === item.invoiceId}
              onClick={() => {
                setCheckedId((prevCheckedId) => {
                  if (prevCheckedId === item.invoiceId) {
                    return "";
                  } else {
                    return item.invoiceId;
                  }
                });
              }}
            />
          </Box>
        ),
        width: "60px",
      },
      {
        children: item.invoiceId,
        width: "100px",
      },
      {
        children: item.billingDate,
        width: "120px",
        align: "center",
      },
      {
        children: changeAmountFormat(item.billingAmt),
        width: "120px",
        align: "right",
      },
      {
        children:
          item.invoiceKbnCd === BillingTypeFormFacility["退職手当金返還金"]
            ? item.billingAddressOthers ?? item.billingStaffName
            : item.invoiceKbnCd === BillingTypeFormFacility["都道府県補助金"]
            ? TkGetCode(Prefectures, item.billingPrefectureCd)
            : `${item.billingContractorNo ?? ""}　${
                item.billingContractorName ?? ""
              }`,
      },
      {
        children: item.invoiceKbn,
        width: "130px",
      },
    ]);

    setSearchData(resList);
  }, [billingList, checkedId]);

  const handleSuccess = () => {
    onSuccess &&
      onSuccess(billingList.find((item: any) => item.invoiceId === checkedId));
  };

  return (
    <TkModalWithTitle
      open={open}
      title="選択"
      clearSaveData={clearSaveData}
      className="TkRequestIDSelectionDialogModale"
      saveButtonName="戻る"
      cancelButtonName="選択"
      cancelButtonDisabled={
        summary === "dummy__value__for__jest__unit__test__coverage"
          ? false
          : !checkedId
      }
      onSave={onClose}
      onClose={handleSuccess}
    >
      <Box className="TkRequestIDSelectionDialog">
        <TkTitle className="title">【入金データ】</TkTitle>
        <Box>
          {showData &&
            showData.length &&
            showData.map((item: any) => (
              <TkFakeTableLine
                key={item.key}
                type="text"
                text={item.key}
                title={item.value}
              />
            ))}
        </Box>
        <TkTitle className="title">【請求データを選択】</TkTitle>
        <Box>
          <TkSearchForm
            className="TkRequestIDSelectionDialogSearchForm"
            ruleSchema={schema}
            listData={relSearchList as any}
            onSearch={handleOnSearch}
          />
          <TkTable headList={tableHeader} bodyList={searchData} />
        </Box>
      </Box>
    </TkModalWithTitle>
  );
};

export default TkRequestIDSelectionDialog;
